import { createApp, defineAsyncComponent } from "vue";
import { createI18n } from "vue-i18n";
import { createAnalytics } from "@/plugins/analytics";
import { useGlobalStore } from "@/stores/global-pinia.js";
import { qModEvent } from "@/utils/eventemitter";
import { createPinia } from "pinia";
import PrimeVue from "primevue/config";
import ToastService from "primevue/toastservice";
import { qmTheme } from "@/assets/qmTheme";
import Tooltip from "primevue/tooltip";
import { primevueLocales } from "@/config/primevue-locales";

import { QModPerformanceMetrics } from "@/plugins/metrics";

async function loadLocale(locale) {
    const module = await import(`@/locales/${locale}.json`);
    return module.default ?? module;
}

const App = defineAsyncComponent(() => import("./QModII.vue"));

const locales = ["en", "fr"];

const sharedPinia = createPinia();

export async function init(
    qmWMID,
    qmComponentDOMElement,
    qmComponentName,
    qmComponentProps,
    qmLanguage = "en",
    qmEnv = "app",
    qmSID = null,
    qmExtra = {}
) {
    const messages = {};
    messages[qmLanguage] = await loadLocale(qmLanguage);

    const Analytics = createAnalytics(qmExtra.Analytics ? qmExtra.Analytics : {});

    const i18n = createI18n({
        locale: qmLanguage,
        messages,
        legacy: false, // you must set `false`, to use Composition API
        globalInjection: true,
    });

    // Create Pinia instance first
    const qModII = createApp(App, {
        qmWMID: qmWMID,
        qmDOMid: qmComponentDOMElement.replace("#", ""),
        qmComponentName: qmComponentName,
        qmComponentProps: qmComponentProps,
        qmEnv,
        qmSID,
    });

    qModII.use(sharedPinia);

    // Initialize Pinia global store (must be after use(sharedPinia))
    const globalStore = useGlobalStore();
    globalStore.initialize({
        wmid: qmWMID,
        env: qmEnv,
        sid: qmSID,
        lang: qmLanguage,
        metrics: new QModPerformanceMetrics(),
    });

    globalStore.setDefaultSelection({
        portfolioId: qmExtra?.defaultPortfolioId ?? qmExtra?.defaultSelection?.portfolioId ?? null,
        tradeAccountId:
            qmExtra?.defaultTradeAccountId ?? qmExtra?.defaultSelection?.tradeAccountId ?? null,
    });

    qModII.directive("voiceoverFocus", (el, binding) => {
        var focusInterval = 10; // ms, time between function calls
        var focusTotalRepetitions = 10; // number of repetitions

        el.setAttribute("tabindex", "0");
        el.blur();

        var focusRepetitions = 0;
        var interval = window.setInterval(function () {
            el.focus();
            focusRepetitions++;
            if (focusRepetitions >= focusTotalRepetitions) {
                window.clearInterval(interval);
            }
        }, focusInterval);
    });

    qModII.use(i18n);
    qModII.use(Analytics);

    // Use PrimeVue with the custom theme and locale
    console.log(qmLanguage)
    qModII.use(ToastService);
    qModII.use(PrimeVue, {
        // unstyled: true,
        theme: {
            preset: qmTheme,
            options: {
                prefix: "qmodii",
                darkModeSelector: ".qmodii-theme-dark",
                cssLayer: false,
            },
        },
        locale: primevueLocales[qmLanguage] || primevueLocales.en,
    });

    qModII.directive("tooltip", Tooltip);

    qModII.mount(qmComponentDOMElement);
}

export function changeLang(lang) {
    qModII.$i18n.locale = lang;
    // Update PrimeVue locale when language changes
    if (qModII.config.globalProperties.$primevue) {
        qModII.config.globalProperties.$primevue.config.locale =
            primevueLocales[lang] || primevueLocales.en;
    }
}

export function setTheme(theme) {
    qModTheme = theme;
}

if (document.readyState != "loading") {
    document.addEventListener(
        "DOMContentLoaded",
        () => {
            qModEvent({
                type: "LOADER",
                event: "FINISHED",
            });
        },
        false
    );
} else {
    qModEvent({
        type: "LOADER",
        event: "FINISHED",
    });
}

window.qModII = window.qModII || {};
window.qModII.init = init;
